import React, { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, FolderTree, Loader2, ChevronRight } from 'lucide-react';
import { AdminAPI, resolveImage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import ImageUpload from './ImageUpload';

const emptyForm = { name: '', icon: '', image: '', parent_id: '' };

const CategoriesTab = ({ categories, onRefresh, loading }) => {
  const { toast } = useToast();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  // Build tree: root categories with their children attached
  const tree = useMemo(() => {
    const roots = categories.filter(c => !c.parent_id);
    const byParent = {};
    categories.forEach(c => {
      if (c.parent_id) {
        if (!byParent[c.parent_id]) byParent[c.parent_id] = [];
        byParent[c.parent_id].push(c);
      }
    });
    return roots.map(r => ({ ...r, children: byParent[r.id] || [] }));
  }, [categories]);

  const rootCategories = useMemo(() => categories.filter(c => !c.parent_id), [categories]);

  const openNew = (parentId = '') => { setForm({ ...emptyForm, parent_id: parentId }); setEditing('new'); };
  const openEdit = (c) => { setForm({ name: c.name, icon: c.icon || '', image: c.image || '', parent_id: c.parent_id || '' }); setEditing(c); };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim()) { toast({ title: 'Nom requis' }); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        icon: form.icon || null,
        image: form.image || null,
        parent_id: form.parent_id || null,
      };
      if (editing === 'new') {
        await AdminAPI.createCategory(payload);
        toast({ title: '✅ Catégorie créée' });
      } else {
        await AdminAPI.updateCategory(editing.id, payload);
        toast({ title: '✅ Catégorie mise à jour' });
      }
      close();
      onRefresh();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie "${c.name}" ?`)) return;
    try {
      await AdminAPI.deleteCategory(c.id);
      toast({ title: 'Catégorie supprimée' });
      onRefresh();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression échouée' });
    }
  };

  // Compact card renderer (sub-categories use the same component, lighter style)
  const CategoryCard = ({ c, isChild = false }) => (
    <div className={`bg-white border border-[#E8E0D0] rounded-xl p-3 flex items-center gap-3 ${isChild ? 'bg-[#FAF7F2]' : ''}`} data-testid={`admin-category-card-${c.id}`}>
      <div className={`${isChild ? 'w-10 h-10' : 'w-12 h-12'} rounded-lg bg-[#F5F1EA] flex items-center justify-center text-xl flex-shrink-0 overflow-hidden`}>
        {c.image
          ? <img src={resolveImage(c.image)} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
          : (c.icon || <FolderTree className="w-5 h-5 text-[#1F4D3F]" />)}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-[#2D2A26] truncate ${isChild ? 'text-sm' : ''}`}>{c.name}</div>
        <div className="text-xs text-[#7A6F5C]">{c.products_count} produit{c.products_count > 1 ? 's' : ''}</div>
      </div>
      <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-[#F5F1EA] rounded text-[#1F4D3F]" title="Modifier" data-testid={`admin-category-edit-${c.id}`}><Edit2 className="w-4 h-4" /></button>
      <button onClick={() => remove(c)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" title="Supprimer" data-testid={`admin-category-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[#7A6F5C]">
          {tree.length} catégorie{tree.length > 1 ? 's' : ''} principales · {categories.length - tree.length} sous-catégorie{categories.length - tree.length > 1 ? 's' : ''}
        </div>
        <Button onClick={() => openNew('')} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="admin-categories-new">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle catégorie
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...</div>
      ) : (
        <div className="space-y-4" data-testid="admin-categories-tree">
          {tree.map(parent => (
            <div key={parent.id} className="bg-[#F5F1EA] border border-[#E8E0D0] rounded-xl p-3">
              <CategoryCard c={parent} />
              {/* Subcategory zone */}
              <div className="mt-2 ml-6 space-y-2">
                {parent.children.map(child => (
                  <div key={child.id} className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-[#7A6F5C] mt-3 flex-shrink-0" />
                    <div className="flex-1">
                      <CategoryCard c={child} isChild />
                    </div>
                  </div>
                ))}
                <button onClick={() => openNew(parent.id)}
                  data-testid={`admin-category-add-child-${parent.id}`}
                  className="ml-6 text-xs text-[#1F4D3F] hover:underline inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Ajouter une sous-catégorie
                </button>
              </div>
            </div>
          ))}
          {tree.length === 0 && (
            <div className="p-10 text-center text-[#7A6F5C] bg-white border border-[#E8E0D0] rounded-xl">Aucune catégorie</div>
          )}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Nouvelle catégorie' : 'Modifier la catégorie'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Catégorie parente</label>
              <select
                value={form.parent_id}
                onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                data-testid="admin-category-form-parent"
                className="w-full h-10 px-3 rounded-md border border-[#E8E0D0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]">
                <option value="">— Catégorie racine —</option>
                {rootCategories
                  .filter(c => !editing || editing === 'new' || c.id !== editing.id)
                  .map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </select>
              <div className="text-xs text-[#7A6F5C] mt-1">Laissez vide pour créer une catégorie racine. Sinon choisissez le parent.</div>
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Nom *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Composites légers" data-testid="admin-category-form-name" />
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Icône emoji</label>
              <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="🦷 ✨ 🔬 ..." maxLength={4} data-testid="admin-category-form-icon" />
              <div className="text-xs text-[#7A6F5C] mt-1">Astuce : copiez un emoji depuis Google ou utilisez l'image ci-dessous</div>
            </div>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Image (optionnel — remplace l'icône)" testId="admin-category-form-image" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close}>Annuler</Button>
            <Button onClick={save} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="admin-category-form-save">
              {busy ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesTab;
